import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { LOGO_URL } from '../mock';
import { Button } from './ui/button';

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 backdrop-blur-sm text-[13px] font-medium overflow-hidden">
      {['en', 'cs'].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 tracking-wide transition-colors ${
            lang === l ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
          }`}
          aria-pressed={lang === l}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: '/', label: t.nav.home },
    { to: '/services', label: t.nav.services },
    { to: '/fleet', label: t.nav.fleet },
    { to: '/about', label: t.nav.about },
    { to: '/contact', label: t.nav.contact },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-md border-b border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04)]' : 'bg-white/60 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[96px] flex items-center justify-between">
        <Link to="/" className="flex items-center group" aria-label="VenaTerra s.r.o. — Home">
          <img
            src={LOGO_URL}
            alt="VenaTerra — Blood of Logistics"
            className="h-24 md:h-28 w-auto object-contain"
            loading="eager"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) =>
                `relative px-3.5 py-2 text-[14px] font-medium transition-colors ${
                  isActive ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-1.5 left-0 right-0 mx-auto h-[2px] w-5 bg-red-500 rounded-full" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LangSwitch />
          <Link to="/contact">
            <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-5 h-10 font-medium shadow-[0_6px_16px_-8px_rgba(29,78,216,0.55)]">
              {t.cta.requestQuote}
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>

        <button
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-700 hover:bg-slate-100"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 bg-white border-b border-slate-200 ${
          open ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) =>
                `px-3 py-2.5 rounded-md text-[15px] font-medium ${
                  isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex items-center justify-between pt-3">
            <LangSwitch />
            <Link to="/contact">
              <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-5 h-10 font-medium">
                {t.cta.requestQuote}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
